import React from "react";
import styled, { keyframes } from "styled-components";
import { duration, easing } from "../../../design-system/tokens/transitions";
import { spacing } from "../../../design-system/tokens/spacing";
import {
  SkeletonBar,
  SkeletonRow,
} from "../../organisms/StoryBuilder/StoryBuilder.skeleton.styles";

const enter = keyframes`
  from { opacity: 0; transform: translateY(${8}px); }
  to { opacity: 1; transform: translateY(0); }
`;

/** Settled content enters over the page-transition duration; reduced motion shows it at once. */
const Enter = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  animation: ${enter} ${duration.normal} ${easing.smooth};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Shell = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  padding: ${spacing[6]};
`;

/** Generic page skeleton (title, subtitle, content blocks) shown while a route chunk loads. */
export const PageSkeleton: React.FC<{ label?: string }> = ({
  label = "Loading",
}) => (
  <Shell role="status" aria-busy="true" aria-label={label}>
    <SkeletonRow>
      <SkeletonBar $w="30%" $h={28} />
      <SkeletonBar $w="50%" $h={14} />
    </SkeletonRow>
    <SkeletonBar $h={120} />
    <SkeletonBar $h={120} />
    <SkeletonBar $h={120} />
  </Shell>
);

/** Suspense with a page skeleton fallback and a 250ms enter once the route has loaded. */
export const PageSuspense: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <React.Suspense fallback={<PageSkeleton />}>
    <Enter>{children}</Enter>
  </React.Suspense>
);
